import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2, ZoomIn, ZoomOut, RotateCcw, Building2, Users, User } from "lucide-react";
import { useGet } from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { api, errMsg } from "../lib/api";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { RecordDialog } from "./RecordDialog";
import { layoutChart, CARD_W } from "./orgLayout";

const KIND_ICON = { person: User, team: Users, external: Building2 };
const KINDS = [{ value: "person", label: "Person" }, { value: "team", label: "Team / division" }, { value: "external", label: "External company (e.g. Finances)" }];

function Card({ n, edit, act, h }) {
  const Icon = KIND_ICON[n.kind];
  const ext = n.kind === "external";
  return (
    <div style={{ width: CARD_W, height: h }} className={`flex flex-col justify-center rounded-md border px-2.5 py-1 text-center ${n.kind === "team" ? "border-[#C2A462]/70 bg-[#18233b]" : ext ? "border-dashed border-sky-400/50 bg-[#0f1b2d]" : "border-white/15 bg-[#111a2c]"}`} data-testid={`org-node-${n.id}`}>
      <div className="flex items-center justify-center gap-1.5 text-[12.5px] font-semibold leading-tight text-slate-100" title={n.display_title}><Icon className={`h-3.5 w-3.5 shrink-0 ${ext ? "text-sky-300" : "text-[#C2A462]"}`} /><span className="line-clamp-2">{n.display_title}</span></div>
      {n.display_sub && <div className={`mt-0.5 line-clamp-2 text-[11px] leading-tight ${ext ? "text-sky-200/80" : "text-[#EBD6A0]/75"}`} title={n.display_sub}>{n.display_sub}</div>}
      {edit && <div className="mt-1 flex justify-center gap-0.5 border-t border-white/10 pt-1">
        {[["left", ChevronLeft, () => act("move", n, -1)], ["right", ChevronRight, () => act("move", n, 1)], ["edit", Pencil, () => act("edit", n)], ["add", Plus, () => act("add", n)], ["delete", Trash2, () => act("delete", n)]].map(([k, I, fn]) => (
          <button key={k} onClick={fn} title={k} className="rounded p-0.5 text-slate-400 hover:bg-[#C2A462]/20 hover:text-[#EBD6A0]" data-testid={`org-${k}-${n.id}`}><I className="h-3.5 w-3.5" /></button>
        ))}
      </div>}
    </div>
  );
}

function Chart({ name, roots, kids, edit, act }) {
  const H = edit ? 92 : 66;
  const ROW = H + 30;
  const L = layoutChart(roots, kids);
  const X = (x) => x + L.shift;
  const lines = [];
  L.edges.forEach((e, i) => {
    const y1 = e.pd * ROW + H;
    const ym = y1 + 15;
    lines.push(<path key={`e${i}`} d={`M${X(e.px)} ${y1} V${ym} H${X(e.cx)} V${e.cd * ROW}`} />);
  });
  L.stacks.forEach((s, i) => {
    if (!s.items.length) return;
    const y1 = s.fromD * ROW + H;
    const last = s.items[s.items.length - 1].d * ROW + H / 2;
    lines.push(<path key={`s${i}`} d={`M${X(s.fromX)} ${y1} V${y1 + 8} H${X(s.lineX)} V${last}`} />);
    s.items.forEach((it, j) => lines.push(<path key={`s${i}-${j}`} d={`M${X(s.lineX)} ${it.d * ROW + H / 2} H${X(it.left)}`} />));
  });
  return (
    <div className="relative" style={{ width: L.width, height: (L.depth + 1) * ROW }}>
      <svg className="pointer-events-none absolute inset-0" width={L.width} height={(L.depth + 1) * ROW} fill="none" stroke="rgba(194,164,98,0.85)" strokeWidth="1">{lines}</svg>
      {L.nodes.map((p) => p.n.kind === "root" ? (
        <div key="root" className="absolute flex items-center justify-center whitespace-nowrap rounded-md border border-[#C2A462] bg-[#0b1428] px-5 text-lg font-bold text-[#C2A462]" style={{ left: X(p.x), top: 0, height: H, transform: "translateX(-50%)", fontFamily: "'Cormorant Garamond', serif" }}>{name}</div>
      ) : (
        <div key={p.n.id} className="absolute" style={{ left: X(p.x) - CARD_W / 2, top: p.d * ROW }}><Card n={p.n} edit={edit} act={act} h={H} /></div>
      ))}
    </div>
  );
}

function ManageBar({ data, reload, edit, setEdit, onAdd, toggle: showToggle }) {
  const toggle = async (v) => {
    try { await api.put("/org-chart/active", { active: v }); toast.success(v ? "Org Chart now shows in every staff sidebar" : "Org Chart hidden from staff sidebars"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reset = async () => {
    if (!window.confirm("Rebuild the chart from your current divisions and people? Your manual changes will be replaced.")) return;
    try { await api.post("/org-chart/reset"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-[#C2A462]/30 bg-[#16213a] p-3" data-testid="org-manage-bar">
      {showToggle && <label className="flex items-center gap-2 text-sm"><Switch checked={data.active} onCheckedChange={toggle} data-testid="org-chart-active" />Active · show Organisation DNA (Org Chart and Mission) in every staff member's sidebar</label>}
      <div className="ml-auto flex flex-wrap gap-2">
        <Button size="sm" variant={edit ? "default" : "secondary"} onClick={() => setEdit(!edit)} data-testid="org-edit-toggle"><Pencil className="h-3.5 w-3.5 mr-1" />{edit ? "Done editing" : "Edit chart"}</Button>
        {edit && <Button size="sm" variant="secondary" onClick={onAdd} data-testid="org-add-top"><Plus className="h-3.5 w-3.5 mr-1" />Add card</Button>}
        {edit && <Button size="sm" variant="ghost" onClick={reset} data-testid="org-reset"><RotateCcw className="h-3.5 w-3.5 mr-1" />Rebuild from divisions</Button>}
      </div>
      {edit && <p className="w-full text-[11px] text-slate-400">Use the controls on each card: arrows change order, the pencil changes "Reports to" or stacks the cards below it, and + adds a card underneath. The top owner card comes from Settings → Business profile → Entity owner. <a className="text-[#C2A462] hover:underline" href="/app/settings" data-testid="orgchart-settings-link">Settings</a></p>}
    </div>
  );
}

export function OrgChartTab({ readOnly = false, inSettings = false }) {
  const { account } = useAuth();
  const [data, reload] = useGet("/org-chart");
  const [edit, setEdit] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [dlg, setDlg] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const measure = (el) => { if (el && (el.offsetWidth !== size.w || el.offsetHeight !== size.h)) setSize({ w: el.offsetWidth, h: el.offsetHeight }); };
  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  const kids = {};
  data.nodes.forEach((n) => { (kids[n.parent_id || "root"] = kids[n.parent_id || "root"] || []).push(n); });
  const below = (id) => [id, ...(kids[id] || []).flatMap((c) => below(c.id))];
  const act = async (what, n, dir) => {
    if (what === "move") { try { await api.post(`/org-chart/nodes/${n.id}/move`, { dir }); reload(); } catch (e) { toast.error(errMsg(e)); } }
    if (what === "edit") setDlg(n);
    if (what === "add") setDlg({ kind: "person", parent_id: n.id });
    if (what === "delete" && window.confirm(`Remove "${n.display_title}"? Cards under it move up a level.`)) { try { await api.delete(`/org-chart/nodes/${n.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } }
  };
  const blocked = dlg?.id ? below(dlg.id) : [];
  const fields = [
    { key: "kind", label: "Card type", type: "select", options: KINDS, required: true, full: true },
    { key: "user_id", label: "Person", type: "select", options: data.people.map((p) => ({ value: p.id, label: `${p.name}${p.position ? ` · ${p.position}` : ""}` })), show: (f) => f.kind === "person", full: true },
    { key: "title", label: "Title (e.g. NDIS, Finances)", show: (f) => f.kind !== "person" },
    { key: "subtitle", label: "Subtitle", show: (f) => f.kind === "team" },
    { key: "subtitle", label: "Company name(s), e.g. ABC Accounting", show: (f) => f.kind === "external" },
    { key: "subtitle", label: "Position shown (blank = their position)", show: (f) => f.kind === "person" },
    { key: "parent_id", label: "Reports to", type: "select", full: true, options: data.nodes.filter((x) => !blocked.includes(x.id)).map((x) => ({ value: x.id, label: `${x.display_title}${x.display_sub ? ` — ${x.display_sub}` : ""}` })) },
    { key: "stack", label: "Stack the cards below this one in a list (saves space)", type: "checkbox", full: true },
  ];
  const save = async (f) => {
    const body = { kind: f.kind, title: f.title || "", subtitle: f.subtitle || "", user_id: f.user_id || null, parent_id: f.parent_id || null, stack: !!f.stack };
    try { dlg.id ? await api.put(`/org-chart/nodes/${dlg.id}`, body) : await api.post("/org-chart/nodes", body); reload(); } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  return (
    <div className="space-y-4" data-testid="org-chart">
      {data.manager && !readOnly && <ManageBar toggle={false} data={data} reload={reload} edit={edit} setEdit={setEdit} onAdd={() => setDlg({ kind: "team", parent_id: "" })} />}
      <div className="flex items-center justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => setZoom((z) => Math.max(0.4, z - 0.1))} data-testid="org-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
        <span className="w-10 text-center text-xs text-slate-400" data-testid="org-zoom-level">{Math.round(zoom * 100)}%</span>
        <Button size="icon" variant="ghost" onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))} data-testid="org-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
      </div>
      <div className="overflow-auto rounded-md border border-white/10 bg-[#0b1220] p-6" data-testid="org-canvas">
        <div className="mx-auto" style={{ width: size.w * zoom, height: size.h * zoom }}>
          <div ref={measure} className="w-max origin-top-left" style={{ transform: `scale(${zoom})` }}>
            <Chart name={account?.name || "Organisation"} roots={kids.root || []} kids={kids} edit={edit} act={act} />
          </div>
        </div>
        {!data.nodes.length && <div className="text-center text-sm text-slate-400">No cards yet{data.manager ? " — click Edit chart, then Add card." : "."}</div>}
      </div>
      {dlg && <RecordDialog open onOpenChange={(o) => !o && setDlg(null)} title={dlg.id ? "Edit card" : "Add card"} fields={fields}
        initial={{ ...dlg, parent_id: dlg.parent_id || "" }} onSave={save} />}
    </div>
  );
}

export default function OrgChartPage() {
  return (
    <div data-testid="org-chart-page">
      <div className="mb-6"><div className="overline">Organisation</div><h1 className="text-4xl sm:text-5xl font-bold" data-testid="page-title">Org Chart</h1></div>
      <OrgChartTab readOnly />
    </div>
  );
}
